import { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Lazy-geladene Seiten (v.a. die schweren Leaflet-Karten-Chunks) scheitern auf
 * Handys gern beim Nachladen: flakiges Netz oder ein nach einem Deploy gecachtes
 * altes index.html, das auf nicht mehr existierende Chunk-Dateien zeigt. Ohne
 * Boundary endet das in einer komplett weißen Seite. Solche Chunk-Fehler heilen
 * wir einmalig per Reload (lädt das frische index.html), alles andere zeigen wir
 * als sichtbaren, meldbaren Fehler statt einer leeren Seite.
 */
const CHUNK_ERROR_RE =
  /Loading chunk|dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported module/i;
const RELOAD_FLAG = "kneipster-chunk-reloaded";

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    // Chunk-Ladefehler: einmal neu laden, um das aktuelle index.html zu holen.
    if (
      CHUNK_ERROR_RE.test(error.message) &&
      !sessionStorage.getItem(RELOAD_FLAG)
    ) {
      sessionStorage.setItem(RELOAD_FLAG, "1");
      window.location.reload();
      return;
    }
    console.error("Uncaught error in route:", error);
  }

  private handleReload = () => {
    sessionStorage.removeItem(RELOAD_FLAG);
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
          <AlertTriangle className="h-7 w-7 text-red-500" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            Diese Seite konnte nicht geladen werden
          </h1>
          <p className="mt-1 max-w-md text-sm text-gray-500">
            Bitte lade die Seite neu. Bleibt das Problem bestehen, gib den
            folgenden Hinweis an die Entwicklung weiter.
          </p>
        </div>
        <pre className="max-w-md overflow-x-auto rounded-lg bg-gray-900 px-3 py-2 text-left text-xs text-gray-100">
          {error.message || String(error)}
        </pre>
        <button
          onClick={this.handleReload}
          className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
        >
          <RefreshCw className="h-4 w-4" />
          Neu laden
        </button>
      </div>
    );
  }
}
